import { useState } from 'react';

import { Quote } from '@core/api';
import {
  ImportantDocumentsSection,
  storage,
  useOnboarding2,
  useTrackEvent
} from '@features/onboarding-2';
import { removeCountryCode, useToggle } from '@lib/utils';
import { NextPageWithLayout } from '@pages/_app';
import { ActionModal } from '@components/index';
import { useFeature } from '@core/feature-toggles';

import { useSummaryPageTranslation } from '../../translation';
import ChangeDetailsModal from './components/ChangeDetailsModal';
import ConditionalPayLaterButton from './components/ConditionalPayLaterButton';
import FormSection from './components/FormSection/';
import QuoteResultSection from './components/OuoteResultSection/QuoteResultSection';
import PayLaterModal from './components/PayLaterModal';
import { getDefaultFormData } from './formData';
import { getQuoteResultProps } from './getQuoteResultProps';
import useCalculateQuoteRequest from './hooks/useCalculateQuoteRequest';
import useCreatePaymentRequest from './hooks/useCreatePaymentRequest';
import useFormSetup from './hooks/useFormSetup';
import useIsClient from './hooks/useIsClient';
import { getUnit } from './paymentCalculations';
import styles from './Summary.module.css';
import { type FormData } from './types';
import { useExistingInsuranceModalCheck } from './hooks/useExistingInsuranceModalCheck';
import { FREE_INSURANCE } from '../../constants';
import useCreateFreeInsuranceRequest from './hooks/useCreateFreeInsuranceRequest';

const transformCalculateQuoteResponse = (
  calculateQuoteResponse: Quote,
  formData: FormData
) => {
  return {
    ...calculateQuoteResponse,
    userInfo: {
      ...calculateQuoteResponse.userInfo,
      emailId: formData.email,
      countryCallingCode: '46',
      mobileNumber: formData.mobileNumber
        ? removeCountryCode(formData.mobileNumber)
        : formData.mobileNumber
    },
    homeData: {
      ...calculateQuoteResponse.homeData,
      paymentPeriodId: Number(formData.paymentPeriodId)
    }
  };
};

const Summary: NextPageWithLayout = () => {
  const isOnboarding2On = useOnboarding2();
  const { trackEvent } = useTrackEvent();
  const { enabled: isFreeInsuranceEnabled } = useFeature('free-insurance');

  const t = useSummaryPageTranslation();
  const [isPayLaterModalOpen, openPayLaterModal, closePayLaterModal] =
    useToggle();
  const [
    isChangeDetaiLsModalOpen,
    openChangeDetailsModal,
    closeChangeDetailsModal
  ] = useToggle();

  const [isExistingInsuranceModalOpen, setIsExistingInsuranceModalOpen] =
    useState(false);

  const isClient = useIsClient();

  const { register, getValues, watch, handleSubmit, formState } = useFormSetup({
    ...getDefaultFormData(),
    ...storage().onboarding
  });

  const paymentPeriodId = watch('paymentPeriodId');

  const { pay: payStandard, isLoading: isStandardLoading } =
    useCreatePaymentRequest();
  const { pay: payFreeInsurance, isLoading: isFreeLoading } =
    useCreateFreeInsuranceRequest();
  const isPaymentLoading = isStandardLoading || isFreeLoading;

  const { response: calculateQuoteResponse, isLoading: isQuoteLoading } =
    useCalculateQuoteRequest();

  useExistingInsuranceModalCheck(() => setIsExistingInsuranceModalOpen(true));

  const onSubmit = (formData: FormData) => {
    if (!calculateQuoteResponse) {
      return;
    }

    storage().saveOnboardingStore({
      email: formData.email,
      givenName: calculateQuoteResponse.userInfo.givenName,
      mobileNumber: formData.mobileNumber
    });

    if (paymentPeriodId === '1') {
      trackEvent('web_selected_yearly_payment_onboarding_2');
    } else {
      trackEvent('web_selected_monthly_payment_onboarding_2');
    }

    const quoteToPay: Quote = transformCalculateQuoteResponse(
      calculateQuoteResponse,
      formData
    );

    if (isFreeInsurance && isFreeInsuranceEnabled) {
      payFreeInsurance(quoteToPay);
    } else {
      payStandard(quoteToPay);
    }
  };

  const onOpenPayLaterModal = (formData: FormData) => {
    if (calculateQuoteResponse) {
      storage().saveOnboardingStore({
        email: formData.email,
        givenName: calculateQuoteResponse.userInfo.givenName,
        mobileNumber: formData.mobileNumber,
        paymentPeriodId: formData.paymentPeriodId
      });
      openPayLaterModal();
    }
  };

  const quoteResultProps = getQuoteResultProps(
    calculateQuoteResponse,
    paymentPeriodId
  );

  if (!isClient || !isOnboarding2On) {
    return null; // This is needed due to hydration errors because we're using localStorage to prefill data.
  }

  // TODO: If we extend the free insurance to other campaigns then currently used 2 months free, we need to handle places in Summary where text or function is aligned to two months free:
  const isFreeInsurance =
    isFreeInsuranceEnabled && quoteResultProps?.campaignCode === FREE_INSURANCE;

  return (
    <div className={styles.summaryPage}>
      <FormSection
        register={register}
        formData={getValues()}
        onChangeDetailsClick={openChangeDetailsModal}
        errors={formState.errors}
        isFreeInsurance={isFreeInsurance}
      />

      <QuoteResultSection
        {...quoteResultProps}
        policyStartDate={
          quoteResultProps?.policyStartDate ?? new Date().toISOString()
        }
        unit={getUnit(paymentPeriodId, {
          monthlyText: t('PRICE_PER_MONTH'),
          yearlyText: t('PRICE_PER_YEAR')
        })}
        paymentPeriodId={paymentPeriodId}
        isButtonDisabled={!formState.isValid}
        handlePayment={handleSubmit(onSubmit)}
        register={register}
        isQuoteLoading={isQuoteLoading}
        renderExtraButton={() => (
          <ConditionalPayLaterButton
            isDisabled={!formState.isValid}
            paymentPeriodId={paymentPeriodId}
            onClick={handleSubmit(onOpenPayLaterModal)}
          />
        )}
        isPaymentLoading={isPaymentLoading}
        isFreeInsurance={isFreeInsurance}
      />

      <ImportantDocumentsSection />

      <PayLaterModal
        isOpen={isPayLaterModalOpen}
        closeModal={closePayLaterModal}
        formData={
          calculateQuoteResponse &&
          transformCalculateQuoteResponse(calculateQuoteResponse, getValues())
        }
      />

      <ChangeDetailsModal
        isOpen={!isPayLaterModalOpen && isChangeDetaiLsModalOpen}
        closeModal={closeChangeDetailsModal}
      />

      {isExistingInsuranceModalOpen && (
        <ActionModal
          title={t('ACTIVE_EXISTING_INSURANCE_MODAL.TITLE')}
          description={t('ACTIVE_EXISTING_INSURANCE_MODAL.DESCRIPTION')}
          onPrimaryClick={() => setIsExistingInsuranceModalOpen(false)}
          onSecondaryClick={() => setIsExistingInsuranceModalOpen(false)}
          primaryButtonText={t('ACTIVE_EXISTING_INSURANCE_MODAL.CONTINUE')}
          secondaryButtonText={t('ACTIVE_EXISTING_INSURANCE_MODAL.CANCEL')}
        />
      )}
    </div>
  );
};

export default Summary;
